import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { erc20Abi, getAddress, isAddressEqual, zeroAddress } from 'viem';

import { HYPER_EVM_CHAIN_ID, useWallet } from '@/features/wallet';
import {
    CUSTODIAL_WALLET_MANAGERS,
    LEGACY_BALANCE_TOKENS,
    previousCustodialManager,
} from '@/lib/config/custodialManagers';
import { hyperEvmPublicClient } from '@/lib/hyperEvmPublicClient';

/**
 * The forward mapping every CustodialWalletManager exposes: owner EOA to its
 * deployed wallet, zero address when that owner has none.
 *
 * Kept local (rather than imported from a shared ABI module) so deleting this
 * hook, the banner and the two mounts removes the whole legacy-withdraw feature.
 */
const CUSTODIAL_MANAGER_WALLETS_ABI = [
    {
        type: 'function',
        name: 'wallets',
        stateMutability: 'view',
        inputs: [{ name: '', type: 'address' }],
        outputs: [{ name: '', type: 'address' }],
    },
] as const;

/**
 * Native below this is not worth a transaction: it would not even cover the gas
 * of the withdrawal that moves it. 10^12 wei is 0.000001 HYPE.
 */
export const MIN_LEGACY_NATIVE_WEI = 1_000_000_000_000n;

/** Eligibility barely moves; the banner refetches explicitly around a withdrawal. */
const LEGACY_FUNDS_STALE_TIME_MS = 30_000;

/**
 * Poll cadence while the user has an old wallet but no new one yet: wallet
 * creation happens during onboarding in another feature, which knows nothing
 * about this query, so without polling the cached `newWallet: null` would hide
 * the banner until a remount or window refocus. Only this tiny cohort polls;
 * everyone else gets no interval at all.
 */
const AWAITING_NEW_WALLET_POLL_MS = 15_000;

export interface LegacyBalance {
    /** ERC20 address, or null for native HYPE. */
    address: `0x${string}` | null;
    symbol: string;
    decimals: number;
    amount: bigint;
}

export interface LegacyWalletFunds {
    /** The user's wallet on the PREVIOUS manager, null when they never had one. */
    oldWallet: `0x${string}` | null;
    /** The user's wallet on the CURRENT manager (the withdrawal destination). */
    newWallet: `0x${string}` | null;
    /** Nonzero balances only, native last. */
    balances: LegacyBalance[];
}

export interface UseLegacyWalletFundsResult extends LegacyWalletFunds {
    /** Old wallet holds something AND a new wallet exists to receive it. */
    eligible: boolean;
    isLoading: boolean;
    /** Re-read now and hand back the fresh snapshot (null when disabled). */
    refetch: () => Promise<LegacyWalletFunds | null>;
}

const EMPTY: LegacyWalletFunds = {
    oldWallet: null,
    newWallet: null,
    balances: [],
};

/** `manager.wallets(owner)`, normalised to null when the owner has no wallet. */
async function readManagerWallet(
    manager: `0x${string}`,
    owner: `0x${string}`,
): Promise<`0x${string}` | null> {
    const wallet = await hyperEvmPublicClient.readContract({
        address: manager,
        abi: CUSTODIAL_MANAGER_WALLETS_ABI,
        functionName: 'wallets',
        args: [owner],
    });
    return isAddressEqual(wallet, zeroAddress) ? null : getAddress(wallet);
}

async function readLegacyWalletFunds(
    owner: `0x${string}`,
    previousManager: `0x${string}`,
    currentManager: `0x${string}`,
): Promise<LegacyWalletFunds> {
    const [oldWallet, newWallet] = await Promise.all([
        readManagerWallet(previousManager, owner),
        readManagerWallet(currentManager, owner),
    ]);
    // no old wallet means nothing to move; no new wallet means there is nowhere
    // to move it yet (the user finishes onboarding first).
    if (!oldWallet || !newWallet) {
        return { oldWallet, newWallet, balances: [] };
    }

    const tokens = LEGACY_BALANCE_TOKENS[HYPER_EVM_CHAIN_ID] ?? [];
    // individual reads on purpose: the HyperEVM chain definition in
    // lib/hyperEvmPublicClient.ts declares no multicall3 contract, so
    // `client.multicall` would throw, and the candidate list is three entries.
    const [native, tokenAmounts] = await Promise.all([
        hyperEvmPublicClient.getBalance({ address: oldWallet }),
        Promise.all(
            tokens.map(async (token) =>
                hyperEvmPublicClient.readContract({
                    address: token.address,
                    abi: erc20Abi,
                    functionName: 'balanceOf',
                    args: [oldWallet],
                }),
            ),
        ),
    ]);

    const balances: LegacyBalance[] = [];
    tokens.forEach((token, index) => {
        const amount = tokenAmounts[index];
        if (amount > 0n) {
            balances.push({
                address: token.address,
                symbol: token.symbol,
                decimals: token.decimals,
                amount,
            });
        }
    });
    // native goes LAST so the withdrawal sequence spends it on gas only after
    // every token leg has been estimated and mined.
    if (native >= MIN_LEGACY_NATIVE_WEI) {
        balances.push({
            address: null,
            symbol: 'HYPE',
            decimals: 18,
            amount: native,
        });
    }

    return { oldWallet, newWallet, balances };
}

/**
 * What the connected EOA can still withdraw from its custodial wallet on the
 * PREVIOUS CustodialWalletManager, after a switchover.
 *
 * Entirely client-side: every CustodialWallet exposes owner-only
 * `withdrawNative` / `withdrawErc20` that need no signature, no manager call and
 * no unpaused state, and the wallet's owner (the connected EOA) never changes.
 * So this hook only has to find the old wallet, price what is in it, and find
 * the new wallet to send it to.
 *
 * Inert unless `lib/config/custodialManagers.ts` declares a `previous` manager
 * for HyperEVM: with none, the query is disabled and issues zero reads.
 */
export function useLegacyWalletFunds(): UseLegacyWalletFundsResult {
    const { address } = useWallet();
    const previousManager = previousCustodialManager(HYPER_EVM_CHAIN_ID);
    const currentManager =
        CUSTODIAL_WALLET_MANAGERS[HYPER_EVM_CHAIN_ID].current;
    const owner = address ? getAddress(address) : null;

    const query = useQuery({
        queryKey: [
            'custodial',
            'legacyWalletFunds',
            HYPER_EVM_CHAIN_ID,
            previousManager,
            owner,
        ],
        queryFn: async () =>
            readLegacyWalletFunds(owner!, previousManager!, currentManager),
        enabled: !!previousManager && !!owner,
        staleTime: LEGACY_FUNDS_STALE_TIME_MS,
        refetchInterval: (q) => {
            const data = q.state.data;
            return data?.oldWallet && !data.newWallet
                ? AWAITING_NEW_WALLET_POLL_MS
                : false;
        },
    });

    const { refetch: refetchQuery } = query;
    const refetch = useCallback(async () => {
        // throwOnError: a failed refetch would otherwise RESOLVE with the
        // previous cached data, and the caller uses this for the click-time
        // "fresh amounts" re-read before submitting withdrawals.
        const { data } = await refetchQuery({ throwOnError: true });
        return data ?? null;
    }, [refetchQuery]);

    const funds = query.data ?? EMPTY;

    return {
        ...funds,
        eligible:
            !!funds.oldWallet && !!funds.newWallet && funds.balances.length > 0,
        isLoading: query.isLoading,
        refetch,
    };
}
